import React, { useState } from "react";
import { Modal, Table, Spinner } from "react-bootstrap";
import { baseUrl } from "../../../../../services/config";
import "./table.css";

// final_status -> badge colors (per API spec §3)
const STATUS_COLORS = {
  present: { bg: "#d4edda", color: "#155724" }, // green
  late_coming: { bg: "#fff3cd", color: "#856404" }, // yellow
  missing: { bg: "#f8d7da", color: "#721c24" }, // red
  absent: { bg: "#f5c6cb", color: "#721c24" }, // red
  off_day: { bg: "#e2e3e5", color: "#383d41" }, // grey
  manual_leave: { bg: "#cce5ff", color: "#004085" }, // blue
  sandwich_absent: { bg: "#ffe0cc", color: "#a04100" }, // orange
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-muted">—</span>;
  const cfg = STATUS_COLORS[status] || { bg: "#e2e3e5", color: "#383d41" };
  const label = status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const PreviewModal = ({
  salaryList,
  show,
  hide,
  campusName,
  setsalaryList,
  salaryMonth,
  yearId,
  authtoken,
  onGenerate,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  // local draft values for the days input, keyed by employee_id
  const [dayDrafts, setDayDrafts] = useState({});
  // collected overrides to send on Generate, keyed by employee_id
  const [dayOverrides, setDayOverrides] = useState({});
  const [recalculating, setRecalculating] = useState({});

  const getFeeAmount = () => {
    let sum = 0;
    salaryList.forEach((element) => {
      sum += parseInt(element.net_pay || 0);
    });
    return sum;
  };

  const selectRow = (item) => {
    setSelectedId((prev) => (prev === item.id ? null : item.id));
  };

  // current value shown in the days input (draft -> row value)
  const draftDays = (salary) => {
    const empId = salary.employee_id;
    return dayDrafts[empId] !== undefined ? dayDrafts[empId] : salary.days ?? 0;
  };

  const onDaysInput = (salary, value) => {
    setDayDrafts((prev) => ({ ...prev, [salary.employee_id]: value }));
  };

  // recalc on blur (avoids one API call per keystroke)
  const onDaysCommit = async (salary) => {
    const empId = salary.employee_id;
    const salaryId = salary.id;
    const days = parseInt(draftDays(salary));

    if (isNaN(days) || days < 0 || days > 31) return;
    if (days === parseInt(salary.days)) return; // unchanged, skip

    setRecalculating((prev) => ({ ...prev, [salaryId]: true }));
    try {
      const res = await fetch(
        `${baseUrl}/employee-salaries/preview-recalculate`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify({
            employee_id: empId,
            year_id: yearId,
            salary_month: salaryMonth + "-01",
            days,
          }),
        },
      );
      const data = await res.json();
      // recalc returns a single-row shape (no attendance_summary)
      const payload = data.payload ? data.payload : data;
      if (
        payload &&
        (payload.net_pay !== undefined || data.metadata?.success)
      ) {
        const updated = salaryList.map((s) =>
          s.id === salaryId
            ? {
                ...s,
                ...payload,
                attendance_summary: s.attendance_summary,
                days,
              }
            : s,
        );
        setsalaryList(updated);
        // track override to send on Generate
        setDayOverrides((prev) => ({ ...prev, [empId]: days }));
      }
    } catch (err) {
      console.log("recalc error", err);
    } finally {
      setRecalculating((prev) => ({ ...prev, [salaryId]: false }));
    }
  };

  const renderDaysCell = (salary) => {
    const value = draftDays(salary);
    const attDays = salary.attendance_days;
    const isManual =
      attDays !== undefined &&
      attDays !== null &&
      parseInt(value) !== parseInt(attDays);
    const isLoading = recalculating[salary.id];

    return (
      <td className="tbodytd" style={{ minWidth: 95 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {attDays !== undefined && attDays !== null && (
            <span style={{ fontSize: 10, color: "#666" }}>
              Attendance: {attDays}
            </span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              type="number"
              min={0}
              max={31}
              value={value}
              onChange={(e) => onDaysInput(salary, e.target.value)}
              onBlur={() => onDaysCommit(salary)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 56,
                textAlign: "center",
                border: isManual
                  ? "2px solid var(--primary-btn-color)"
                  : "1px solid #ccc",
                background: isManual ? "#fff7e6" : "#fff",
                borderRadius: 4,
                padding: "2px 4px",
                fontSize: 13,
                fontWeight: 600,
                color: "#000",
              }}
            />
            {isLoading && (
              <Spinner
                size="sm"
                animation="border"
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
            )}
          </div>
        </div>
      </td>
    );
  };

  const renderTableRow = (salary, index) => {
    const rowColor =
      salary.days > 30 ? "green" : salary.days < 30 ? "red" : "inherit";

    return (
      <tr
        onClick={() => selectRow(salary)}
        style={{
          backgroundColor: salary.id === selectedId ? "#D3CECE" : "",
          color: rowColor,
          cursor: "pointer",
        }}
        className="tbodytr text-center"
        key={salary.id}
      >
        <td className="tbodytd fixed">{index + 1}</td>
        <td className="tbodytd fixed2">{salary.employee?.emp_code}</td>
        <td className="tbodytd fixed3">{salary.employee?.full_name}</td>
        <td className="tbodytd adjusting">{salary.employee?.father_name}</td>
        <td className="tbodytd">
          {salary.account_no ? salary.account_no : "-"}
        </td>
        <td className="tbodytd">{salary.basic_pay}</td>
        <td className="tbodytd">{salary.hifz}</td>
        <td className="tbodytd">{salary.hostel}</td>
        <td className="tbodytd">{salary.college}</td>
        <td className="tbodytd">{salary.additional_allowance}</td>
        <td className="tbodytd">{salary.increment}</td>
        <td className="tbodytd">{salary.second_shift}</td>
        <td className="tbodytd">{salary.ugs}</td>
        <td className="tbodytd">{salary.other_allowance}</td>
        <td className="tbodytd">{salary.hod}</td>
        <td className="tbodytd">{salary.science}</td>
        <td className="tbodytd">{salary.extra_period}</td>
        <td className="tbodytd">{salary.disparity_allowance}</td>
        <td className="tbodytd">{salary.convance}</td>
        <td className="tbodytd">{salary.insurance_cont}</td>
        <td className="tbodytd">{salary.gpf_return}</td>
        <td className="tbodytd">{salary.gpf_cont}</td>
        <td
          className="tbodytd text-color-primary"
          style={{ backgroundColor: "darkgray" }}
        >
          {salary.gross_salary}
        </td>
        <td className="tbodytd">{salary.gp_fund}</td>
        <td className="tbodytd">{salary.welfare_fund}</td>
        <td className="tbodytd">{salary.eobi}</td>
        <td className="tbodytd">{salary.income_tax}</td>
        <td className="tbodytd">{salary.loan_refund}</td>
        <td className="tbodytd">{salary.other_deduction}</td>
        <td className="tbodytd">{salary.child_fee_deduction}</td>
        <td className="tbodytd">{salary.insurance}</td>
        <td className="tbodytd">{salary.van_charge}</td>
        <td className="tbodytd">{salary.social_security}</td>
        <td className="tbodytd">{salary.net_pay}</td>
        {renderDaysCell(salary)}
      </tr>
    );
  };

  const sum = (key) =>
    salaryList.reduce((a, c) => a + parseInt(c[key] || 0), 0);

  const selectedSalary = salaryList.find((s) => s.id === selectedId);
  const att = selectedSalary?.attendance_summary;

  return (
    <Modal onHide={hide} size="xl" show={show}>
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Salary List Details
        </Modal.Title>
        <h3 className="text-color-primary ml-4">{campusName?.name}</h3>
        <h4 className="ml-4">Net Payable Salary : {getFeeAmount()}</h4>
        <button onClick={hide} className="btn btn-danger ml-auto">
          Close
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="tablemain">
          <table className="table">
            <thead className="tablehead">
              <tr className="tableheadtr text-center">
                <th className="background-color-primary tableHEadNAme tablehedfixed1">
                  SR.NO
                </th>
                <th className="background-color-primary tableHEadNAme tablehedfixed2">
                  Employee Code
                </th>
                <th className="background-color-primary tableHEadNAme tablehedfixed3">
                  Employee Name
                </th>
                <th className="background-color-primary tableHEadNAme tablehedfixed4">
                  Father Name
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Account No
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Basic Pay
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Hifz Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Hostel Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  College Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Additional Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Increment Total
                </th>
                <th className="background-color-primary tableHEadNAme">
                  SShift Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  USG Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Other Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  HoD Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Science Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Ex.Priod Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Disparity Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Conv Allow
                </th>
                <th className="background-color-primary tableHEadNAme">
                  HI Contribution
                </th>
                <th className="background-color-primary tableHEadNAme">
                  GPF Return
                </th>
                <th className="background-color-primary tableHEadNAme">
                  GPF Contribution
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Gross Salary
                </th>
                <th className="background-color-primary tableHEadNAme">
                  GP Fund
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Welfare Fund
                </th>
                <th className="background-color-primary tableHEadNAme">Eobi</th>
                <th className="background-color-primary tableHEadNAme">
                  Income Tax
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Loan Refund
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Other Deduction
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Child Fee Deduction
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Insu. Deduction
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Van Fee Deduction
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Social Security Deduction
                </th>
                <th className="background-color-primary tableHEadNAme">
                  Net Pay
                </th>
                <th className="background-color-primary tableHEadNAme">Days</th>
              </tr>
            </thead>
            <tbody>
              {salaryList.map((salary, index) => renderTableRow(salary, index))}
              <tr className="tbodytr text-center">
                <td className="tbodytd fixed">-</td>
                <td className="tbodytd fixed2">-</td>
                <td className="tbodytd fixed3">-</td>
                <td className="tbodytd adjusting">Total</td>
                <td className="tbodytd">-</td>
                <td className="tbodytd">{sum("basic_pay")}</td>
                <td className="tbodytd">{sum("hifz")}</td>
                <td className="tbodytd">{sum("hostel")}</td>
                <td className="tbodytd">{sum("college")}</td>
                <td className="tbodytd">{sum("additional_allowance")}</td>
                <td className="tbodytd">{sum("increment")}</td>
                <td className="tbodytd">{sum("second_shift")}</td>
                <td className="tbodytd">{sum("ugs")}</td>
                <td className="tbodytd">{sum("other_allowance")}</td>
                <td className="tbodytd">{sum("hod")}</td>
                <td className="tbodytd">{sum("science")}</td>
                <td className="tbodytd">{sum("extra_period") || "-"}</td>
                <td className="tbodytd">{sum("disparity_allowance") || "-"}</td>
                <td className="tbodytd">{sum("convance")}</td>
                <td className="tbodytd">{sum("insurance_cont")}</td>
                <td className="tbodytd">{sum("gpf_return")}</td>
                <td className="tbodytd">{sum("gpf_cont")}</td>
                <td
                  className="tbodytd text-color-primary"
                  style={{ backgroundColor: "darkgray" }}
                >
                  {sum("gross_salary")}
                </td>
                <td className="tbodytd">{sum("gp_fund")}</td>
                <td className="tbodytd">{sum("welfare_fund")}</td>
                <td className="tbodytd">{sum("eobi")}</td>
                <td className="tbodytd">{sum("income_tax")}</td>
                <td className="tbodytd">{sum("loan_refund")}</td>
                <td className="tbodytd">{sum("other_deduction")}</td>
                <td className="tbodytd">{sum("child_fee_deduction")}</td>
                <td className="tbodytd">{sum("insurance")}</td>
                <td className="tbodytd">{sum("van_charge")}</td>
                <td className="tbodytd">{sum("social_security")}</td>
                <td className="tbodytd">{sum("net_pay")}</td>
                <td className="tbodytd">-</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Per-employee attendance panel — opens on row click, uses in-memory data */}
        {selectedSalary && att && (
          <div
            style={{
              marginTop: 24,
              padding: 20,
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              background: "#fafafa",
            }}
          >
            <h5 style={{ marginBottom: 16, fontWeight: 700 }}>
              Attendance Detail — {selectedSalary.employee?.full_name}
            </h5>

            {att.mode === "fallback" ? (
              <div
                style={{
                  padding: "12px 16px",
                  background: "#fff3cd",
                  border: "1px solid #ffeeba",
                  borderRadius: 6,
                  color: "#856404",
                  fontSize: 13,
                }}
              >
                {att.reason ||
                  "No biometric attendance setup for this employee. Salary calculated on configured salary days."}
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 32,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Salary Days
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {att.stats?.salary_days ?? att.salary_days ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Calculated Absents
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#dc3545",
                      }}
                    >
                      {att.stats?.calculated_absents ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Calculated Leaves
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#007bff",
                      }}
                    >
                      {att.stats?.calculated_leaves ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      Leave Attachment Absent
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#fd7e14",
                      }}
                    >
                      {att.stats?.leave_attachment_absent ?? "—"}
                    </div>
                  </div>
                  {att.leave_balance && (
                    <>
                      <div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          Payable Leave Days
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "#28a745",
                          }}
                        >
                          {att.leave_balance.payable_leave_days}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          Remaining Leaves
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>
                          {att.leave_balance.remaining_leaves}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {att.daily_details?.length > 0 && (
                  <Table responsive bordered size="sm">
                    <thead className="tablehead">
                      <tr className="tableheadtr text-center">
                        <th className="background-color-primary tableHEadNAme">
                          Date
                        </th>
                        <th className="background-color-primary tableHEadNAme">
                          Status
                        </th>
                        <th className="background-color-primary tableHEadNAme">
                          First In
                        </th>
                        <th className="background-color-primary tableHEadNAme">
                          Last Out
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {att.daily_details.map((day, i) => (
                        <tr key={i} className="text-center">
                          <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                            {day.date}
                          </td>
                          <td>
                            <StatusBadge status={day.final_status} />
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {day.first_in || "—"}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {day.last_out || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <small className="text-muted mr-auto" style={{ fontSize: 12 }}>
          Click a row to view attendance. Edit <b>Days</b> (then click away) to
          recalculate that employee's salary.
        </small>
        {onGenerate && (
          <button
            onClick={() => onGenerate(dayOverrides)}
            className="btn btn-success button_blue"
            style={{ width: "auto", minWidth: 120 }}
          >
            Generate Salary
          </button>
        )}
        <button onClick={hide} className="btn btn-danger">
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PreviewModal;
